import React, { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";

import {
  ArrowCircleLeftOutlined,
  ArrowCircleRightOutlined,
} from "@mui/icons-material";
import { normalizeDate } from "../../../utilities";
import { getAllRecognitions } from "../../../components/Forms/api";
import { CSVLink } from "react-csv";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import { selectRoles } from "../../../features/roleSlice";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState } from "../../../components/UI/States";

const RecognitionEntries = () => {
  const [entries, setEntries] = useState([]);
  const [flag, setFlag] = useState(false);

  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);

  useEffect(() => {
    getAllRecognitions(setEntries);
  }, [flag]);

  const deleteForm = async (data) => {
    const formData = new FormData();
    formData.append("id", data._id);
    formData.append("key", "9a8dj98j89JSDn12jmoiamsdp");
    formData.append("deleted_by", user.name);

    await fetch("/api/forms/submit/recognitionForm/delete", {
      method: "POST",
      body: formData,
    });
    setFlag(!flag);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Entry Date",
        accessor: "date_entered",
        Cell: ({ cell: { value }, row: { original } }) => (
          <div className="flex w-28 flex-col items-start gap-1">
            <p className="whitespace-nowrap text-xs tabular-nums">
              {normalizeDate(value)}
            </p>
            {role?.roles?.includes("Admin") && (
              <button
                type="button"
                onClick={() => deleteForm(original)}
                className="rounded-full text-rose-600 hover:underline dark:text-rose-300"
              >
                <p className="text-xs">[Delete Entry]</p>
              </button>
            )}
          </div>
        ),
      },
      {
        Header: "From",
        accessor: "submit_user",
      },
      {
        Header: "For",
        accessor: "employee",
      },
      {
        Header: "Values",
        accessor: "value",
        Cell: ({ cell: { value }, row: { original } }) => {
          return (
            <div className="flex w-36 flex-wrap gap-1">
              {value.map((item, index) => (
                <span
                  key={index}
                  className="whitespace-nowrap rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200"
                >
                  {item}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        Header: "Files",
        accessor: "files",
        Cell: ({ cell: { value }, row: { original } }) => {
          return (
            <div className="flex flex-col">
              {value && value.length > 0 ? (
                value.map((file, index) => (
                  <div key={index}>
                    <a
                      href={`https://node.bigclumber.com/api/forms/submit/recognitionForm/files/${original._id}/${file.filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-link break-all"
                    >
                      {file.filename}
                    </a>
                  </div>
                ))
              ) : (
                <p className="app-muted text-xs">
                  No files uploaded
                </p>
              )}
            </div>
          );
        },
      },
      {
        Header: "Explanation",
        accessor: "explanation",
        Cell: ({ cell: { value }, row: { original } }) => (
          <div className="min-w-[16rem] whitespace-pre-wrap">
            {highlightMatches(value || "", original.value || [])}
          </div>
        ),
      },
    ],
    []
  );

  const sortedEntries = useMemo(
    () =>
      [...entries].sort(
        (first, second) =>
          new Date(second.date_entered).getTime() -
          new Date(first.date_entered).getTime()
      ),
    [entries]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex, pageSize },
    setPageSize,
    prepareRow,
  } = useTable(
    {
      columns,
      data: sortedEntries,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useSortBy,
    usePagination
  );

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={EmojiEventsIcon}
        tone="amber"
        eyebrow="Forms"
        title="Caught in the Act Entries"
        description="Browse recognition submissions, download them as CSV, or remove an entry if necessary. Sorting and pagination keep the newest submissions easy to find."
        actions={
          <CSVLink
            target="_blank"
            data={sortedEntries}
            className="app-btn app-btn-secondary"
          >
            <FileDownloadIcon sx={{ fontSize: 18 }} />
            Download CSV
          </CSVLink>
        }
      />

      <div className="app-card overflow-hidden rounded-2xl border">
        <div className="app-divider flex items-center border-b px-4 py-3">
          <span className="app-eyebrow tabular-nums">
            Total Entries: {entries.length}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table {...getTableProps()} className="app-table">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="cursor-pointer select-none hover:text-[color:var(--app-text)]"
                    >
                      <div className="flex items-center gap-1">
                        {column.render("Header")}
                        <span className="text-xs">
                          {column.isSorted ? (column.isSortedDesc ? "↓" : "↑") : ""}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.length === 0 ? (
                <tr>
                  <td colSpan={columns.length}>
                    <EmptyState
                      compact
                      icon={EmojiEventsIcon}
                      title="No entries have been submitted yet."
                      description="Caught in the Act recognitions will show up here."
                    />
                  </td>
                </tr>
              ) : (
                page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          className="whitespace-pre-wrap !align-top"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="app-surface flex flex-col gap-3 border-t px-4 py-3 text-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="app-btn app-btn-secondary app-btn-sm"
              >
                <ArrowCircleLeftOutlined className="!text-base" /> Prev
              </button>
              <button
                type="button"
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="app-btn app-btn-secondary app-btn-sm"
              >
                Next <ArrowCircleRightOutlined className="!text-base" />
              </button>
            </div>

            <div className="app-muted flex flex-wrap items-center gap-2 text-xs tabular-nums">
              <span>
                Page {pageIndex + 1} of {pageOptions.length || 1}
              </span>
              <span className="hidden sm:inline">•</span>
              <span>
                Showing {entries.length === 0 ? 0 : pageIndex * pageSize + 1}–
                {Math.min(entries.length, (pageIndex + 1) * pageSize)} of
                {" "}
                {entries.length}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="app-muted">Rows per page:</span>
            <div className="app-card flex rounded-full border p-0.5">
              {[5, 10, 20, 50].map((size) => (
                <button
                  key={size}
                  type="button"
                  aria-pressed={pageSize === size}
                  onClick={() => setPageSize(size)}
                  className={`min-w-[2.25rem] rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums transition ${
                    pageSize === size
                      ? "bg-sky-600 text-white"
                      : "hover:bg-[color:var(--app-hover)]"
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default RecognitionEntries;

const highlightMatches = (text, values) => {
  if (!text) return null;

  const valueSet = new Set(values.map((item) => item.toLowerCase()));

  return text.split(/(\b[\w'-]+\b)/g).map((part, index) => {
    const normalized = part.replace(/[^\w'-]/g, "").toLowerCase();

    if (normalized && valueSet.has(normalized)) {
      return (
        <mark
          key={index}
          className="rounded bg-amber-300/60 px-1 font-semibold text-amber-950 dark:bg-amber-400/30 dark:text-amber-100"
        >
          {part}
        </mark>
      );
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};
